/**
 * Browser compatibility utilities for the booking system
 * Provides polyfills and feature detection for cross-browser support
 */

// Feature detection utilities
export const browserSupport = {
  // Check if browser supports modern date input
  supportsDateInput(): boolean {
    const input = document.createElement('input')
    input.setAttribute('type', 'date')
    return input.type === 'date'
  },

  // Check if browser supports CSS Grid
  supportsCSSGrid(): boolean {
    return CSS.supports('display', 'grid')
  },

  // Check if browser supports CSS custom properties (variables)
  supportsCSSCustomProperties(): boolean {
    return CSS.supports('color', 'var(--test)')
  },

  // Check if browser supports Intersection Observer
  supportsIntersectionObserver(): boolean {
    return 'IntersectionObserver' in window
  },

  // Check if browser supports localStorage
  supportsLocalStorage(): boolean {
    try {
      const test = 'test'
      localStorage.setItem(test, test)
      localStorage.removeItem(test)
      return true
    } catch (e) {
      return false
    }
  },

  // Check if browser supports fetch API
  supportsFetch(): boolean {
    return 'fetch' in window
  },

  // Check if browser supports Promise
  supportsPromise(): boolean {
    return 'Promise' in window
  },

  // Check if browser supports ES6 modules
  supportsES6Modules(): boolean {
    const script = document.createElement('script')
    return 'noModule' in script
  }
}

// Polyfills and fallbacks
export const polyfills = {
  // Add focus-visible polyfill for better keyboard navigation
  addFocusVisibleSupport(): void {
    if (!CSS.supports('selector(:focus-visible)')) {
      // Add focus-visible class manually for older browsers
      document.addEventListener('keydown', () => {
        document.body.classList.add('using-keyboard')
      })
      
      document.addEventListener('mousedown', () => {
        document.body.classList.remove('using-keyboard')
      })
    }
  },

  // Ensure smooth scrolling works across browsers
  addSmoothScrollSupport(): void {
    if (!CSS.supports('scroll-behavior', 'smooth')) {
      // Add polyfill for smooth scrolling
      const links = document.querySelectorAll('a[href^="#"]')
      links.forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault()
          const target = document.querySelector((link as HTMLAnchorElement).getAttribute('href')!)
          if (target) {
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      })
    }
  }
}

// Browser-specific CSS classes for styling adjustments
export const browserClasses = {
  // Add browser-specific classes to body
  addBrowserClasses(): void {
    const ua = navigator.userAgent.toLowerCase()
    const body = document.body

    // Safari-specific
    if (ua.includes('safari') && !ua.includes('chrome')) {
      body.classList.add('browser-safari')
    }

    // Chrome-specific
    if (ua.includes('chrome') && !ua.includes('edge')) {
      body.classList.add('browser-chrome')
    }

    // Firefox-specific
    if (ua.includes('firefox')) {
      body.classList.add('browser-firefox')
    }

    // Edge-specific
    if (ua.includes('edge') || ua.includes('edg/')) {
      body.classList.add('browser-edge')
    }

    // iOS-specific
    if (/iPad|iPhone|iPod/.test(ua)) {
      body.classList.add('browser-ios')
    }

    // Android-specific
    if (ua.includes('android')) {
      body.classList.add('browser-android')
    }

    // Mobile detection
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      body.classList.add('is-mobile')
    }

    // Touch device detection
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      body.classList.add('touch-device')
    }
  }
}

// Initialize browser compatibility features
export function initBrowserCompat(): void {
  // Add browser classes
  browserClasses.addBrowserClasses()
  
  // Add polyfills
  polyfills.addFocusVisibleSupport()
  polyfills.addSmoothScrollSupport()
  
  // Log browser support information for debugging
  if (import.meta.env.DEV) {
    console.log('Browser Support:', {
      dateInput: browserSupport.supportsDateInput(),
      cssGrid: browserSupport.supportsCSSGrid(),
      cssCustomProperties: browserSupport.supportsCSSCustomProperties(),
      localStorage: browserSupport.supportsLocalStorage(),
      fetch: browserSupport.supportsFetch(),
      promise: browserSupport.supportsPromise()
    })
  }
}